
import { useNavigate } from "react-router-dom";
import { EnrolledCourse } from "@/hooks/useEnrolledCourses";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useTranslation } from "react-i18next";
import { setLanguagePreference } from "@/lib/languageUtils";

interface CourseCardProps {
  course: EnrolledCourse;
}

export function CourseCard({ course }: CourseCardProps) {
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const handleCourseClick = () => {
    // Check if course language differs from current language
    if (course.lang !== i18n.language) {
      // Switch to the course's language
      i18n.changeLanguage(course.lang);
      // Store the language preference as an explicit user choice
      setLanguagePreference(course.lang as 'en' | 'fr', true);
    }

    // Generate language-aware base path for the course's language
    const basePath = `/${course.lang === 'en' ? '' : course.lang + '/'}courses/${course.slug}`;

    // Determine navigation target based on course type
    let linkTo = basePath;

    // For multi-lesson courses, try to link to first lesson
    if (course.course_type !== 'single-page') {
      const mods = course.modules || [];
      const firstMod = mods.sort((a, b) => a.position - b.position)[0];
      const firstLesson = firstMod?.lessons?.sort((a, b) => a.position - b.position)[0];
      const moduleSlug = firstMod?.id;
      const lessonSlug = firstLesson?.slug;

      if (moduleSlug && lessonSlug) {
        linkTo = `${basePath}/${moduleSlug}/${lessonSlug}`;
      }
    }

    // Navigate to the course
    navigate(linkTo);
  };

  return (
    <div onClick={handleCourseClick} className="group block h-full cursor-pointer">
      <Card className="card-enhanced bg-card rounded-lg overflow-hidden h-full flex flex-col">
        {course.cover && (
          <div className="w-full overflow-hidden">
            <img
              src={course.cover}
              alt={course.title}
              className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300 ease-in-out"
            />
          </div>
        )}
        <div className="p-6 flex flex-col flex-grow">
          <h3 className="text-xl font-bold mb-2">{course.title}</h3>
          <p className="text-muted-foreground mb-4 line-clamp-2 flex-grow">{course.description}</p>
          <div className="mb-4">
            <div className="text-sm mb-1 flex justify-between">
              <span>Progress</span>
              <span className="numeric">{course.progress}%</span>
            </div>
            <Progress value={course.progress} className="h-2" />
          </div>
          {/* Hide price if user has access; show badge if so */}
          {course.hasAccess ? (
            <div className="bg-green-100 px-3 py-1 rounded-full text-sm font-medium text-green-700">Access Granted</div>
          ) : course.is_free ? (
            <div className="bg-primary/10 px-3 py-1 rounded-full text-sm font-medium text-primary">Free</div>
          ) : course.price ? (
            <div className="bg-primary/10 px-3 py-1 rounded-full text-sm font-medium text-primary price">{course.price}</div>
          ) : null}
          {/* Card is clickable; removed explicit Continue Learning link */}
        </div>
      </Card>
    </div>
  );
}
